import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, Lock, ArrowRight, BadgeInfo } from "lucide-react";

const Block = ({ block }) => {
    if (block.type === "cards") {
        return (
            <div>
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518] mb-3">
                    {block.heading}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {block.items.map((it) => (
                        <div key={it.title} className="rounded-xl border border-white/8 bg-[#0A0E17] p-4">
                            <div className="flex items-start justify-between gap-3">
                                <p className="text-sm font-semibold text-white">{it.title}</p>
                                {it.price && (
                                    <span className="shrink-0 font-jbmono text-xs font-bold text-[#F5C518]">
                                        {it.price}
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-xs text-slate-500">{it.line}</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    if (block.type === "list") {
        return (
            <div>
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518] mb-3">
                    {block.heading}
                </p>
                <div className="space-y-2">
                    {block.items.map((it) => (
                        <div
                            key={it.title}
                            className="flex items-center justify-between gap-3 rounded-xl border border-white/8 bg-[#0A0E17] px-4 py-3"
                        >
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-white truncate">{it.title}</p>
                                <p className="text-xs text-slate-500">{it.line}</p>
                            </div>
                            <span className="shrink-0 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-2.5 py-0.5 text-[10px] font-medium text-[#00E5FF]">
                                {it.tag}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    if (block.type === "slots") {
        return (
            <div>
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518] mb-3">
                    {block.heading}
                </p>
                <div className="flex gap-2 mb-3">
                    {block.days.map((day, i) => (
                        <span
                            key={day}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-medium ${
                                i === 0
                                    ? "bg-[#F5C518] text-[#07090E]"
                                    : "border border-white/12 text-slate-400"
                            }`}
                        >
                            {day}
                        </span>
                    ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {block.times.map((t, i) => (
                        <span
                            key={t}
                            className={`rounded-lg px-2 py-2 text-center text-xs font-jbmono ${
                                i === 1
                                    ? "border border-[#00E5FF]/60 bg-[#00E5FF]/10 text-[#00E5FF]"
                                    : "border border-white/10 text-slate-400"
                            }`}
                        >
                            {t}
                        </span>
                    ))}
                </div>
            </div>
        );
    }
    if (block.type === "stats") {
        return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {block.items.map((it) => (
                    <div key={it.label} className="rounded-xl border border-white/8 bg-[#0A0E17] p-4">
                        <p className="font-display text-xl sm:text-2xl font-extrabold text-white">
                            {it.value}
                        </p>
                        <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                            {it.label}
                        </p>
                    </div>
                ))}
            </div>
        );
    }
    if (block.type === "bars") {
        return (
            <div>
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518] mb-3">
                    {block.heading}
                </p>
                <div className="flex items-end gap-2 h-28 rounded-xl border border-white/8 bg-[#0A0E17] p-4">
                    {block.items.map((v, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                            <div
                                className={`w-full rounded-t ${i === 5 ? "bg-[#F5C518]/80" : "bg-[#00E5FF]/50"}`}
                                style={{ height: `${v}%` }}
                            />
                            <span className="text-[9px] text-slate-600 font-jbmono">
                                {block.labels[i]}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    if (block.type === "form") {
        return (
            <div className="rounded-xl border border-white/8 bg-[#0A0E17] p-4 sm:p-5">
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#F5C518] mb-3">
                    {block.heading}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                    {block.fields.map((f) => (
                        <div
                            key={f}
                            className="rounded-lg border border-white/10 bg-[#0D131F] px-3 py-2.5 text-xs text-slate-600"
                        >
                            {f}
                        </div>
                    ))}
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5C518] px-5 py-2.5 text-xs font-semibold text-[#07090E]">
                    {block.cta} <ArrowRight size={13} />
                </span>
            </div>
        );
    }
    return null;
};

export const DemoPreview = ({ name, demo, onClose, onBuild }) => {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <motion.div
            data-testid="demo-preview-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-[#07090E]/88 backdrop-blur-md"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={`${name} demo preview`}
        >
            <motion.div
                initial={{ y: 50, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 40, opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-3xl bg-[#0D131F] border border-white/12 rounded-2xl sm:rounded-3xl overflow-hidden max-h-[90vh] flex flex-col shadow-[0_30px_90px_-20px_rgba(0,0,0,0.8)]"
            >
                <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/10 bg-[#0A0E17] shrink-0">
                    <div className="flex gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                    </div>
                    <div className="flex-1 flex items-center gap-2 rounded-lg border border-white/10 bg-[#07090E] px-3 py-1.5 min-w-0">
                        <Lock size={11} className="text-[#00E5FF] shrink-0" />
                        <span className="font-jbmono text-[11px] text-slate-400 truncate">
                            {demo.url}
                        </span>
                    </div>
                    <span className="hidden sm:inline-flex shrink-0 rounded-full border border-[#F5C518]/40 bg-[#F5C518]/10 px-3 py-1 font-jbmono text-[9px] uppercase tracking-[0.18em] text-[#F5C518]">
                        Demo Concept
                    </span>
                    <button
                        data-testid="demo-preview-close"
                        onClick={onClose}
                        aria-label="Close preview"
                        className="h-8 w-8 shrink-0 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#00E5FF]/50 transition-colors"
                    >
                        <X size={15} />
                    </button>
                </div>

                <div className="overflow-y-auto p-5 sm:p-7 space-y-7">
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-b border-white/8 pb-4">
                        <span className="font-display text-sm font-bold text-white mr-2">
                            {demo.hero.title}
                        </span>
                        {demo.nav.map((n) => (
                            <span key={n} className="text-xs text-slate-500 self-center">
                                {n}
                            </span>
                        ))}
                    </div>

                    <div>
                        <span className="inline-block rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/8 px-3.5 py-1 font-jbmono text-[10px] uppercase tracking-[0.18em] text-[#00E5FF]">
                            {demo.hero.badge}
                        </span>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                            {demo.hero.title}
                        </h3>
                        <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-xl">
                            {demo.hero.sub}
                        </p>
                        <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F5C518] px-6 py-3 text-sm font-semibold text-[#07090E]">
                            {demo.hero.cta} <ArrowRight size={15} />
                        </span>
                    </div>

                    {demo.blocks.map((b, i) => (
                        <Block key={i} block={b} />
                    ))}

                    <div className="flex items-start gap-2.5 rounded-xl border border-white/8 bg-[#07090E] px-4 py-3">
                        <BadgeInfo size={15} className="mt-0.5 shrink-0 text-[#00E5FF]" />
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            Demo Concept — a sample solution designed by Animexa Tech Solutions to
                            show what we can build for this type of business. This is not a real
                            client project; names, prices and data shown are samples.
                        </p>
                    </div>

                    <button
                        data-testid="demo-preview-build-cta"
                        onClick={() => {
                            onClose();
                            onBuild(name);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5C518] px-6 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] active:scale-[0.98]"
                    >
                        Build This For My Business <ArrowRight size={16} />
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};
